import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, Trash2, Plus, ExternalLink, Paperclip } from "lucide-react";
import Seo from "../components/Seo";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AUTH_START = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const STATUS_OPTIONS = ["New", "Contacted", "Quoted", "Won", "Lost"];

export default function Admin() {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("enquiries");

    if (loading) return <div className="min-h-[70vh] flex items-center justify-center font-mono text-xs uppercase text-stone">Checking session…</div>;

    if (!user) return (
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="admin-login">
            <Seo title="Admin" description="Admin console" path="/admin" />
            <div className="font-mono text-xs uppercase tracking-widest text-clay mb-4">[ Restricted Access ]</div>
            <h1 className="font-serif text-6xl text-forest tracking-tight">Sign in.</h1>
            <p className="text-inkgreen/80 mt-4">Google authentication is required. Only whitelisted team members can access the CMS.</p>
            <button onClick={AUTH_START} className="btn-pill btn-solid mt-10 mx-auto" data-testid="admin-google-login">Sign in with Google →</button>
        </div>
    );

    if (user.role !== "admin") return (
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="admin-forbidden">
            <h1 className="font-serif text-5xl text-forest">Access denied.</h1>
            <p className="text-inkgreen/80 mt-4">Your Google account ({user.email}) is not on the admin allowlist. If you should have access, ask a whitelisted admin to add you.</p>
            <button onClick={logout} className="btn-pill mt-8 mx-auto">Sign out</button>
        </div>
    );

    return (
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
            <Seo title="Admin Dashboard" description="Admin console" path="/admin" />
            <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
                <div>
                    <div className="font-mono text-xs uppercase tracking-widest text-clay mb-2">[ Signed in as {user.email} ]</div>
                    <h1 className="font-serif text-5xl text-forest tracking-tight">Admin Console</h1>
                </div>
                <button onClick={logout} className="btn-pill" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Sign out</button>
            </div>

            <div className="flex gap-6 border-b border-forest/30 mb-8">
                {["enquiries", "blog"].map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                        className={`pb-3 text-sm font-mono uppercase tracking-widest border-b-2 transition-colors ${tab === t ? "border-forest text-forest" : "border-transparent text-stone hover:text-forest"}`}>
                        {t}
                    </button>
                ))}
            </div>

            {tab === "enquiries" ? <EnquiriesTab /> : <BlogTab />}
        </div>
    );
}

function EnquiriesTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/enquiries"); setItems(data); }
        catch (_e) { toast.error("Failed to load enquiries"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/enquiries/${id}/status`, { status });
            setItems((arr) => arr.map((x) => x.id === id ? { ...x, status } : x));
            toast.success(`Status: ${status}`);
        } catch (_e) { toast.error("Update failed"); }
    };

    return (
        <div data-testid="admin-enquiries">
            <div className="flex justify-between items-center mb-6">
                <div className="font-mono text-xs uppercase tracking-widest text-stone">{items.length} enquiries</div>
                <a href={`${API}/admin/enquiries/export/csv`} className="btn-pill" data-testid="admin-export-csv"><Download className="w-4 h-4" /> Export CSV</a>
            </div>
            {loading ? <div className="py-10 font-mono text-xs uppercase text-stone">Loading…</div> :
                items.length === 0 ? <div className="py-10 border border-forest/30 text-center text-stone">No enquiries yet.</div> :
                <div className="border border-forest/30 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-sand border-b border-forest/30">
                            <tr>
                                {["Created", "Company", "Contact", "Country", "Business", "Products", "Requirement", "Status", ""].map((h) => (
                                    <th key={h} className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-forest">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} className="border-b border-forest/20 hover:bg-sand/50" data-testid={`enquiry-row-${it.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs">{new Date(it.created_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 font-serif text-lg text-forest">{it.company_name}</td>
                                    <td className="px-4 py-3">{it.contact_person}<br/><span className="text-xs text-stone">{it.email}</span></td>
                                    <td className="px-4 py-3">{it.country}</td>
                                    <td className="px-4 py-3">{it.business_type}</td>
                                    <td className="px-4 py-3 text-xs">{(it.products_interested || []).join(", ")}</td>
                                    <td className="px-4 py-3 text-xs">{it.monthly_requirement}</td>
                                    <td className="px-4 py-3">
                                        <select value={it.status} onChange={(e) => updateStatus(it.id, e.target.value)} data-testid={`enquiry-status-${it.id}`}
                                            className={`text-xs font-mono uppercase px-2 py-1 border ${it.status === 'New' ? 'border-clay text-clay' : it.status === 'Won' ? 'border-forest bg-forest text-bone' : it.status === 'Lost' ? 'border-stone text-stone' : 'border-forest text-forest'}`}>
                                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <button onClick={() => setSelected(it)} className="text-xs underline text-clay" data-testid={`enquiry-view-${it.id}`}>View</button>
                                            {it.file_name && (
                                                <a
                                                    href={`${API}/admin/enquiries/${it.id}/attachment`}
                                                    className="text-clay hover:text-forest transition-colors"
                                                    title={`Download ${it.file_name}`}
                                                    data-testid={`enquiry-attachment-${it.id}`}
                                                >
                                                    <Paperclip className="w-4 h-4" />
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            }
            {selected && <EnquiryModal enquiry={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}

function EnquiryModal({ enquiry, onClose }) {
    const [full, setFull] = useState(null);
    useEffect(() => { api.get(`/admin/enquiries/${enquiry.id}`).then(({ data }) => setFull(data)); }, [enquiry.id]);
    const d = full || enquiry;
    return (
        <div className="fixed inset-0 bg-forest/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="enquiry-modal">
            <div className="bg-bone border border-forest max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                <div className="font-mono text-xs uppercase tracking-widest text-clay mb-2">[ Enquiry · {d.status} ]</div>
                <h2 className="font-serif text-4xl text-forest tracking-tight mb-6">{d.company_name}</h2>
                {[
                    ["Contact", d.contact_person], ["Email", d.email], ["Mobile", d.mobile],
                    ["Country", d.country], ["Business Type", d.business_type],
                    ["Products", (d.products_interested || []).join(", ")],
                    ["Monthly Requirement", d.monthly_requirement],
                    ["Message", d.message],
                    ["Received", new Date(d.created_at).toLocaleString()],
                ].map(([k, v]) => v ? (
                    <div key={k} className="border-t border-forest/20 py-3">
                        <div className="font-mono text-xs uppercase tracking-widest text-stone">{k}</div>
                        <div className="text-inkgreen">{v}</div>
                    </div>
                ) : null)}
                {(d.file_name || d.file_data) && (
                    <div className="border-t border-forest/20 py-3">
                        <div className="font-mono text-xs uppercase tracking-widest text-stone">Attachment</div>
                        <a href={`${API}/admin/enquiries/${d.id}/attachment`} className="inline-flex items-center gap-2 text-clay underline hover:text-forest" data-testid="enquiry-modal-attachment">
                            <Paperclip className="w-4 h-4" /> {d.file_name || "Download file"}
                        </a>
                    </div>
                )}
                <button onClick={onClose} className="btn-pill mt-6">Close</button>
            </div>
        </div>
    );
}

function BlogTab() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | 'new' | postObject

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/blog/posts"); setPosts(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Delete this post?")) return;
        try { await api.delete(`/blog/posts/${id}`); toast.success("Deleted"); load(); } catch (_e) { toast.error("Delete failed"); }
    };

    if (editing) return <BlogEditor post={editing === "new" ? null : editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

    return (
        <div data-testid="admin-blog">
            <div className="flex justify-between items-center mb-6">
                <div className="font-mono text-xs uppercase tracking-widest text-stone">{posts.length} posts</div>
                <button onClick={() => setEditing("new")} className="btn-pill btn-solid" data-testid="admin-new-post"><Plus className="w-4 h-4" /> New Post</button>
            </div>
            {loading ? <div className="py-10 font-mono text-xs uppercase text-stone">Loading…</div> :
                <div className="border border-forest/30">
                    {posts.map((p) => (
                        <div key={p.id} className="border-b border-forest/20 p-4 flex items-center justify-between gap-4">
                            <div>
                                <div className="font-serif text-2xl text-forest">{p.title}</div>
                                <div className="text-xs font-mono uppercase text-stone">{p.slug} · {p.published ? "Published" : "Draft"}</div>
                            </div>
                            <div className="flex gap-2">
                                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="btn-pill" data-testid={`admin-view-${p.slug}`}><ExternalLink className="w-4 h-4" /></a>
                                <button onClick={() => setEditing(p)} className="btn-pill" data-testid={`admin-edit-${p.slug}`}>Edit</button>
                                <button onClick={() => del(p.id)} className="btn-pill" data-testid={`admin-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

function BlogEditor({ post, onDone, onCancel }) {
    const [form, setForm] = useState(post || { title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: [], published: true });
    const [saving, setSaving] = useState(false);
    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, tags: Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map(s => s.trim()).filter(Boolean) };
            if (post) await api.put(`/blog/posts/${post.id}`, payload);
            else await api.post("/blog/posts", payload);
            toast.success("Saved");
            onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    return (
        <div className="grid gap-6 max-w-4xl" data-testid="admin-blog-editor">
            <h2 className="font-serif text-4xl text-forest">{post ? "Edit Post" : "New Post"}</h2>
            <div><label className="font-mono text-xs uppercase text-stone">Title</label><input className="print-input" value={form.title} onChange={upd("title")} data-testid="editor-title" /></div>
            <div><label className="font-mono text-xs uppercase text-stone">Slug (optional)</label><input className="print-input" value={form.slug} onChange={upd("slug")} data-testid="editor-slug" placeholder="auto from title" /></div>
            <div><label className="font-mono text-xs uppercase text-stone">Cover image URL</label><input className="print-input" value={form.cover_image} onChange={upd("cover_image")} data-testid="editor-cover" /></div>
            <div><label className="font-mono text-xs uppercase text-stone">Tags (comma-separated)</label><input className="print-input" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={upd("tags")} data-testid="editor-tags" /></div>
            <div><label className="font-mono text-xs uppercase text-stone">Excerpt</label><textarea rows={2} className="print-input" value={form.excerpt} onChange={upd("excerpt")} data-testid="editor-excerpt" /></div>
            <div><label className="font-mono text-xs uppercase text-stone">Content (Markdown)</label><textarea rows={16} className="print-input font-mono text-sm" value={form.content} onChange={upd("content")} data-testid="editor-content" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="editor-published" /> Published</label>
            <div className="flex gap-3">
                <button onClick={save} disabled={saving} className="btn-pill btn-solid" data-testid="editor-save">{saving ? "Saving…" : "Save"}</button>
                <button onClick={onCancel} className="btn-pill">Cancel</button>
            </div>
        </div>
    );
}
